/**
 * SubjectiveGrades component is responsible for displaying the subjective grading results
 * for a given programming submission. It fetches and displays rubric data and grading history
 * for the selected submission.
 
 */
import log from "loglevel";

import React, { useEffect, useState } from "react";
import * as constants from "Constants";
import httpService from "services/httpService.js";
import displayGlobalMessage from "Utils/DisplayGlobalMessage";
import { Accordion, Card } from "react-bootstrap";
import RubricResults from "../RubricResults/RubricResults.jsx";
import { RiArrowDropDownLine, RiArrowDropRightLine } from "react-icons/ri";

export default function SubjectiveGrades({
  programId,
  selectedSubmission,
  delegate_student_id,
}) {
  const [gradingHistory, setGradingHistory] = useState({});
  const [comment, setComment] = useState("");
  const [rubricData, setRubricData] = useState([]);
  const [totalScore, setTotalScore] = useState(0);
  const [isAccordionOpen, setIsAccordionOpen] = useState(false);

  //This useEffect hook fetches rubric data and grading history for the selected submission
  useEffect(() => {
    const fetchRubricData = async () => {
      const url =
        constants.base_url + "/evalpro/rubric/read_rubric/" + programId;
      try {
        const response = await httpService.get(url);
        let array = [];
        //Creates an array of rubric data objects
        response.data.map((criteria) => {
          array.push({
            criteria_id: criteria.id,
            name: criteria.title,
            description: criteria.description,
            Ratings: criteria.Ratings.map((rating) => ({
              rating_id: rating.id,
              tag: rating.title,
              description: rating.description,
              mark: rating.marks,
            })).sort((a, b) => (a.mark > b.mark ? -1 : 1)),
          });
        });

        setRubricData(array);

        //Calculates the maximum score for each criteria by taking the maximum of all ratings
        const maxScores = [
          ...array.map((criteria) =>
            Math.max(...criteria.Ratings.map((ratings) => ratings.mark))
          ),
        ];
        //Calculates the total score by summing up the maximum scores of all criteria
        setTotalScore(maxScores.reduce((a, b) => a + parseInt(b), 0));
      } catch (error) {
        log.log(error);
        displayGlobalMessage("Reading rubric data failed", "error");
      }
    };

    const fetchGradingHistory = async () => {
      const url =
        constants.base_url +
        "/evalpro/rubric/read_grades/" +
        selectedSubmission.id;
      try {
        const response = await httpService.get(url);
        let currentHistory = { ...gradingHistory };
        response.data.map((history) => {
          currentHistory[history.criteria] = {
            mark: history.marks,
            id: history.rating,
            is_graded_by_ai: history.is_graded_by_ai,
          };
        });
        setGradingHistory(currentHistory);
      } catch (error) {
        log.log(error);
        displayGlobalMessage("Reading grades failed", "error");
      }
      setComment(selectedSubmission.comments);
    };

    fetchRubricData();

    if (selectedSubmission) {
      fetchGradingHistory();
    }
  }, [selectedSubmission]);

  return (
    <Accordion defaultActiveKey="0">
      <Card>
        <Accordion.Toggle
          as={Card.Header}
          onClick={() => setIsAccordionOpen(!isAccordionOpen)}
        >
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div>Grading Results</div>
            <div>
              {isAccordionOpen ? (
                <RiArrowDropDownLine
                  size={35}
                  color="grey"
                  style={{ margin: "-10 0 -10 10" }}
                />
              ) : (
                <RiArrowDropRightLine
                  size={35}
                  color="grey"
                  style={{ margin: "-10 0 -10 10" }}
                />
              )}
            </div>
          </div>
        </Accordion.Toggle>
        <Accordion.Collapse in={isAccordionOpen}>
          <Card.Body>
            <div>
              {rubricData.length === 0 ? (
                <div>Rubric Details are not Available !</div>
              ) : (
                <RubricResults
                  programId={programId}
                  submission={selectedSubmission}
                  readOnly={true}
                  rubricData={rubricData}
                  setRubricData={setRubricData}
                  gradingHistory={gradingHistory}
                  setGradingHistory={setGradingHistory}
                  comment={comment}
                  setComment={setComment}
                  totalScore={totalScore}
                  setTotalScore={setTotalScore}
                />
              )}
            </div>
          </Card.Body>
        </Accordion.Collapse>
      </Card>
    </Accordion>
  );
}

SubjectiveGrades.defaultProps = {
  programId: 0,
  selectedSubmission: {},
  delegate_student_id: null,
};
